import React, { useState, useCallback, Dispatch, SetStateAction, useMemo, useRef } from 'react';
// Fix: Use named imports for Node and useReactFlow from reactflow.
import { Node, useReactFlow } from 'reactflow';
import { AppNode, WorkflowNodeData, NodeCategory } from '../types';
import { NODE_TYPES_CONFIG } from '../constants';

export const useNodeOperations = (
    nodes: AppNode[],
    setNodes: Dispatch<SetStateAction<AppNode[]>>,
    setEdges: Dispatch<SetStateAction<any[]>>
) => {
    const { screenToFlowPosition } = useReactFlow();
    // Store ID instead of object to prevent stale state issues
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTabId, setActiveTabId] = useState<string>('properties');
    
    // Ref to track the node being highlighted as a specific drop target (hover)
    const lastHoveredNodeId = useRef<string | null>(null);
    const isHighlightingPotentialTargets = useRef(false);

    // Derived selected node ensures we always have the latest version from the nodes array
    const selectedNode = useMemo(() => 
        nodes.find(n => n.id === selectedNodeId) || null
    , [nodes, selectedNodeId]);

    // Wrapper to maintain API compatibility
    const setSelectedNode = useCallback((node: AppNode | null) => {
        setSelectedNodeId(node ? node.id : null);
    }, []);

    const openModalAtTab = useCallback((nodeId: string, tabId: string = 'properties') => {
        setSelectedNodeId(nodeId);
        setActiveTabId(tabId);
        setIsModalOpen(true);
    }, []);

    const clearHighlights = useCallback(() => {
        setNodes(nds => nds.map(n => {
            if (n.data.isBoundaryCandidate || n.data.isPotentialBoundaryTarget) {
                return { 
                    ...n, 
                    data: { 
                        ...n.data, 
                        isBoundaryCandidate: false, 
                        isPotentialBoundaryTarget: false 
                    } 
                };
            }
            return n;
        }));
        lastHoveredNodeId.current = null;
        isHighlightingPotentialTargets.current = false;
    }, [setNodes]);

    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';

        // @ts-ignore - Accessing global set by Sidebar
        const type = window.__draggingNodeType;
        if (!type) return;

        const config = NODE_TYPES_CONFIG[type];
        const isBoundary = config?.category === NodeCategory.Boundary;

        if (isBoundary) {
            // 1. Highlight all potential targets once if we haven't yet
            if (!isHighlightingPotentialTargets.current) {
                isHighlightingPotentialTargets.current = true;
                setNodes(nds => nds.map(n => {
                    const targetConfig = NODE_TYPES_CONFIG[n.type || ''];
                    const isEligible = targetConfig && (
                        targetConfig.category === NodeCategory.Task || 
                        targetConfig.category === NodeCategory.Activity
                    );
                    if (isEligible) {
                        return { ...n, data: { ...n.data, isPotentialBoundaryTarget: true } };
                    }
                    return n;
                }));
            }

            // 2. Highlight specific node under cursor
            const target = event.target as HTMLElement;
            const nodeElement = target.closest('.react-flow__node');
            const targetNodeId = nodeElement?.getAttribute('data-id');

            if (targetNodeId && targetNodeId !== lastHoveredNodeId.current) {
                const targetNode = nodes.find(n => n.id === targetNodeId);
                const targetConfig = targetNode ? NODE_TYPES_CONFIG[targetNode.type || ''] : null;
                
                const isEligible = targetConfig && (
                    targetConfig.category === NodeCategory.Task || 
                    targetConfig.category === NodeCategory.Activity
                );

                if (isEligible) {
                    setNodes(nds => nds.map(n => {
                        // Clear old hover, set new hover
                        if (n.id === lastHoveredNodeId.current) {
                            return { ...n, data: { ...n.data, isBoundaryCandidate: false } };
                        }
                        if (n.id === targetNodeId) {
                            return { ...n, data: { ...n.data, isBoundaryCandidate: true } };
                        }
                        return n;
                    }));
                    lastHoveredNodeId.current = targetNodeId;
                } else {
                    if (lastHoveredNodeId.current) {
                        setNodes(nds => nds.map(n => 
                            n.id === lastHoveredNodeId.current 
                            ? { ...n, data: { ...n.data, isBoundaryCandidate: false } } 
                            : n
                        ));
                        lastHoveredNodeId.current = null;
                    }
                }
            } else if (!targetNodeId && lastHoveredNodeId.current) {
                 setNodes(nds => nds.map(n => 
                    n.id === lastHoveredNodeId.current 
                    ? { ...n, data: { ...n.data, isBoundaryCandidate: false } } 
                    : n
                ));
                lastHoveredNodeId.current = null;
            }
        }
    }, [nodes, setNodes]);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            
            const type = event.dataTransfer.getData('application/reactflow');
            if (typeof type === 'undefined' || !type) {
                clearHighlights();
                return;
            }

            const position = screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            const config = NODE_TYPES_CONFIG[type];
            const isBoundary = config?.category === NodeCategory.Boundary;

            // Find if we are dropping on a node
            const target = event.target as HTMLElement;
            const nodeElement = target.closest('.react-flow__node');
            const targetNodeId = nodeElement?.getAttribute('data-id');
            const targetNode = nodes.find(n => n.id === targetNodeId);
            const targetConfig = targetNode ? NODE_TYPES_CONFIG[targetNode.type || ''] : null;

            // Specific Logic for Boundary Events
            if (isBoundary) {
                const isEligible = targetNode && (
                    targetConfig?.category === NodeCategory.Task || 
                    targetConfig?.category === NodeCategory.Activity
                );

                if (!isEligible) {
                    clearHighlights();
                    return;
                }

                // Calculate position relative to parent to place it UNDER the bottom border
                // Parent width is typically 220px, height 64px.
                // Boundary event circle is 56px wide/high (w-14 h-14).
                const relativeX = 110 - 28; // Center x relative to parent width (220/2 - 56/2)
                const relativeY = 64;       // Start child Y at bottom of parent

                const boundaryNodeId = `node_${Date.now()}`;
                const newBoundaryNode: AppNode = {
                    id: boundaryNodeId,
                    type,
                    position: { x: relativeX, y: relativeY },
                    parentNode: targetNodeId!,
                    zIndex: 2000,
                    data: {
                        label: config?.label || type,
                        status: 'idle',
                        cancelActivity: true
                    },
                    // Crucial: do not set extent: 'parent' so it is not clipped/confined
                };

                setNodes((nds) => nds.concat(newBoundaryNode));

                // Auto-create Association Edge
                const edgeId = `e_assoc_${targetNodeId}_${boundaryNodeId}`;
                setEdges((eds) => [
                    ...eds,
                    {
                        id: edgeId,
                        source: targetNodeId!,
                        target: boundaryNodeId,
                        sourceHandle: 'boundary',
                        targetHandle: null,
                        type: 'default',
                        animated: false,
                        style: { stroke: '#94a3b8' },
                    },
                ]);
                
                clearHighlights();
                return;
            }

            // Standard Node Placement
            if (config) {
                let width = 220;
                let height = 64;
                position.x -= width / 2;
                position.y -= height / 2;
            }

            const initialData: WorkflowNodeData = {
                label: config?.label || type,
                status: 'idle'
            };

            // Preset data for specific types
            if (type === 'FormStartEvent' || type === 'UserTask') {
                initialData.formSchema = { fields: [] };
                initialData.formTitle = 'New Form';
            }
            if (type === 'ServiceTaskREST') {
                initialData.method = 'GET';
                initialData.resultVariable = 'apiResult';
                initialData.headers = { 'Content-Type': 'application/json' };
            }
            if (type === 'XorGateway' || type === 'InclusiveGateway') {
                initialData.outputs = ['Path 1'];
                initialData.conditions = [{ path: 0, label: 'Path 1', expression: '', description: 'Default path' }];
                initialData.evaluationOrder = type === 'InclusiveGateway' ? 'parallel' : 'sequential';
            }

            const newNode: AppNode = {
                id: `node_${Date.now()}`,
                type,
                position,
                data: initialData,
            };

            setNodes((nds) => nds.concat(newNode));
            clearHighlights();
        },
        [setNodes, setEdges, screenToFlowPosition, nodes, clearHighlights]
    );

    const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
        setSelectedNodeId(node.id);
    }, []);

    const onPaneClick = useCallback(() => {
        setSelectedNodeId(null);
    }, []);

    const onNodeDoubleClick = useCallback((_: React.MouseEvent, node: Node) => {
        setSelectedNodeId(node.id);
        setActiveTabId('properties');
        setIsModalOpen(true);
    }, []);

    const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node) => {
        event.preventDefault();
        setNodes((nds) =>
            nds.map((n) => {
                if (n.id === node.id) {
                    const hasBreakpoint = !n.data.hasBreakpoint;
                    return { ...n, data: { ...n.data, hasBreakpoint } };
                }
                return n;
            })
        );
    }, [setNodes]);

    const updateNodeData = useCallback((id: string, newData: Partial<WorkflowNodeData>) => {
        setNodes((nds) =>
            nds.map((node) => {
                if (node.id === id) {
                    const updatedNode = { ...node, data: { ...node.data, ...newData } };

                    if (newData.outputs && Array.isArray(newData.outputs)) {
                        setEdges(currentEdges => currentEdges.map(edge => {
                            if (edge.source === id && edge.sourceHandle) {
                                const index = parseInt(edge.sourceHandle);
                                if (!isNaN(index) && newData.outputs && newData.outputs[index]) {
                                    return { ...edge, label: newData.outputs[index] };
                                }
                            }
                            return edge;
                        }));
                    }

                    return updatedNode;
                }
                return node;
            })
        );
    }, [setNodes, setEdges]);

    return {
        selectedNode,
        setSelectedNode,
        isModalOpen,
        setIsModalOpen,
        activeTabId,
        setActiveTabId,
        openModalAtTab,
        onDragOver,
        onDrop,
        onNodeClick,
        onPaneClick,
        onNodeDoubleClick,
        onNodeContextMenu,
        updateNodeData,
        clearHighlights
    };
};